import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { extractTextFromPDF } from '../utils/pdfExtractor';

function buildTaxYears() {
  const current = new Date().getFullYear();
  return [String(current), String(current - 1)];
}
const TAX_YEARS = buildTaxYears();

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia',
  'Austria', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium',
  'Bolivia', 'Bosnia and Herzegovina', 'Brazil', 'Bulgaria', 'Cambodia',
  'Canada', 'Chile', 'China', 'Colombia', 'Croatia', 'Cyprus', 'Czech Republic',
  'Denmark', 'Ecuador', 'Egypt', 'Estonia', 'Ethiopia', 'Finland', 'France',
  'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala', 'Honduras', 'Hungary',
  'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait', 'Latvia',
  'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia', 'Mexico', 'Moldova',
  'Morocco', 'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Pakistan',
  'Panama', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Saudi Arabia', 'Serbia', 'Singapore', 'Slovakia', 'Slovenia',
  'South Africa', 'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland',
  'Taiwan', 'Thailand', 'Tunisia', 'Turkey', 'Ukraine', 'United Arab Emirates',
  'United Kingdom', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam',
];

// Common business types with their NAICS codes
// Shown as a simple picker so users never have to google a code
const BUSINESS_PRESETS = [
  { label: 'Consulting / Advisory',         activity: 'Consulting Services',              code: '541690' },
  { label: 'Software / IT Services',        activity: 'Software & IT Services',           code: '541511' },
  { label: 'SaaS / Software Product',       activity: 'Software Publishing',              code: '511210' },
  { label: 'Digital Marketing / Agency',    activity: 'Advertising & Marketing Services', code: '541810' },
  { label: 'E-commerce / Online Retail',    activity: 'Electronic Shopping & Retail',     code: '454110' },
  { label: 'Content Creator / Influencer',  activity: 'Internet Publishing & Broadcasting', code: '519130' },
  { label: 'Coaching / Online Courses',     activity: 'Educational Instruction Services', code: '611699' },
  { label: 'Freelance / Creative Services', activity: 'Independent Artists & Writers',    code: '711510' },
  { label: 'Import / Export / Trading',     activity: 'Wholesale Trade & Distribution',   code: '425120' },
  { label: 'Other / General Business',      activity: 'Other Professional Services',      code: '541990' },
];

function isValidEIN(raw) {
  return String(raw || '').replace(/\D/g, '').length === 9;
}

function formatEINInput(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

export function FilingForm({
  user,
  usageStats,
  planTracker,
  formData,
  setFormData,
  isProcessing,
  processingError,
  onSubmit,
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [dragOver,   setDragOver  ] = useState(false);
  const [fileError,  setFileError ] = useState('');
  const [isParsing,  setIsParsing ] = useState(false);
  const [einTouched, setEINTouched] = useState(false);

  const handle = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleEIN = (e) => {
    const formatted = formatEINInput(e.target.value);
    setFormData((prev) => ({ ...prev, ein: formatted }));
  };

  // When user picks a business preset, auto-fill both activity and code
  const handlePreset = (e) => {
    const preset = BUSINESS_PRESETS.find(p => p.code === e.target.value);
    if (!preset) return;
    setFormData(prev => ({
      ...prev,
      businessActivity: preset.activity,
      businessCode:     preset.code,
      _businessPreset:  preset.code,
    }));
  };

  const handleFile = async (file) => {
    setFileError('');
    if (!file) return;
    if (file.type !== 'application/pdf') { setFileError('Please upload a PDF file.'); return; }
    if (file.size > 10 * 1024 * 1024)   { setFileError('File must be under 10MB.');  return; }
    setIsParsing(true);
    try {
      const text = await extractTextFromPDF(file);
      setFormData((prev) => ({
        ...prev,
        statementText: text,
        statementName: file.name,
        statementSize: file.size,
      }));
    } catch (err) {
      setFileError(err.message);
    } finally {
      setIsParsing(false);
    }
  };

  const onFileInput = (e) => handleFile(e.target.files[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, statementText: null, statementName: '', statementSize: 0 }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024)         return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const einValid = isValidEIN(formData.ein);

  const isFormValid =
    formData.llcName?.trim() &&
    formData.ein?.trim() && einValid &&
    formData.taxYear?.trim() &&
    formData.ownerName?.trim() &&
    formData.ownerCountry?.trim() &&
    formData.businessActivity?.trim() &&
    formData.businessCode?.trim() &&
    formData.statementText;

  const planLoading   = planTracker?.loading ?? true;
  const hasPurchased  = !planLoading && (planTracker?.hasPurchased?.() ?? false);
  const usedAllFiling = !planLoading && hasPurchased && usageStats && !usageStats.canGenerate;
const submitBlocked = !planLoading && user && !hasPurchased && !planTracker?.hasFreeFilingLeft?.();

  return (
    <div className="form-page">
      <div className="form-page-inner">

        {/* ── Hero ─────────────────────────────────────────────── */}
        <div className="form-hero">
          <div className="form-hero-left">
            <div className="form-eyebrow">
              <span className="eyebrow-pulse" />
              IRS Form 5472 · Foreign-Owned LLC
            </div>
            <h1 className="form-title">
              File your Form 5472 <em>without the $25,000 penalty</em>
            </h1>
            <p className="form-subtitle">
              Upload your bank statement. We extract the transactions, fill the
              form, and fax it directly to the IRS. Delivery confirmed.
            </p>
          </div>
        </div>

        {/* ── Two-column grid: LLC info + Owner info ────────────── */}
        <div className="form-top-grid">

          {/* LEFT: LLC panel */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">Your LLC</span>
            </div>
            <div className="form-panel-body">

              <div className="form-group">
                <label className="form-label">LLC legal name</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Acme Solutions LLC"
                  value={formData.llcName || ''}
                  onChange={handle('llcName')}
                  disabled={isProcessing}
                />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">EIN</label>
                  <input
                    className={`form-input${einTouched && formData.ein && !einValid ? ' form-input--error' : ''}`}
                    type="text"
                    placeholder="12-3456789"
                    value={formData.ein || ''}
                    onChange={handleEIN}
                    onBlur={() => setEINTouched(true)}
                    disabled={isProcessing}
                    maxLength={10}
                    inputMode="numeric"
                  />
                  {einTouched && formData.ein && !einValid && (
                    <span className="form-field-error">EIN must be 9 digits</span>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Tax year</label>
                  <select
                    className="form-input form-select"
                    value={formData.taxYear || ''}
                    onChange={handle('taxYear')}
                    disabled={isProcessing}
                  >
                    <option value="">Select year</option>
                    {TAX_YEARS.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Business type — simple preset picker, no NAICS knowledge needed */}
              <div className="form-group">
                <label className="form-label">What does your LLC do?</label>
                <select
                  className="form-input form-select"
                  value={formData._businessPreset || formData.businessCode || ''}
                  onChange={handlePreset}
                  disabled={isProcessing}
                >
                  <option value="">Select your business type</option>
                  {BUSINESS_PRESETS.map(p => (
                    <option key={p.code} value={p.code}>{p.label}</option>
                  ))}
                </select>
                <span className="form-field-hint">
                  This fills in the business activity fields on your form automatically.
                </span>
              </div>

              {/* Show the resolved values so user can verify / edit if needed */}
              {formData.businessCode && (
                <div className="form-preset-confirm">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  <span>Activity: <strong>{formData.businessActivity}</strong> · Code: <strong>{formData.businessCode}</strong></span>
                  <button
                    type="button"
                    className="form-preset-edit-btn"
                    onClick={() => setFormData(p => ({ ...p, _showManual: !p._showManual }))}
                  >
                    {formData._showManual ? 'Hide' : 'Edit'}
                  </button>
                </div>
              )}

              {/* Manual override — hidden by default, shown only if user clicks Edit */}
              {formData._showManual && (
                <div className="form-row-2">
                  <div className="form-group">
                    <label className="form-label">Activity description</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formData.businessActivity || ''}
                      onChange={handle('businessActivity')}
                      disabled={isProcessing}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">NAICS code</label>
                    <input
                      className="form-input"
                      type="text"
                      value={formData.businessCode || ''}
                      onChange={handle('businessCode')}
                      disabled={isProcessing}
                      maxLength={6}
                      inputMode="numeric"
                    />
                  </div>
                </div>
              )}

              <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <input
                  type="checkbox"
                  id="isFirstYear"
                  checked={formData.isFirstYear || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, isFirstYear: e.target.checked }))}
                  disabled={isProcessing}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label
                  htmlFor="isFirstYear"
                  className="form-label"
                  style={{ textTransform: 'none', letterSpacing: 0, cursor: 'pointer', marginBottom: 0 }}
                >
                  This is my first year filing Form 5472 for this LLC
                </label>
              </div>

            </div>
          </div>

          {/* RIGHT: Owner panel */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">About you (foreign owner)</span>
            </div>
            <div className="form-panel-body">

              <div className="form-group">
                <label className="form-label">Your full name</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Jan Müller"
                  value={formData.ownerName || ''}
                  onChange={handle('ownerName')}
                  disabled={isProcessing}
                />
              </div>

              {/* Email — moved up, more important than country for UX flow */}
              <div className="form-group">
                <label className="form-label">Your email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.ownerEmail || ''}
                  onChange={handle('ownerEmail')}
                  disabled={isProcessing}
                />
                <span className="form-field-hint">We'll send your fax confirmation here.</span>
              </div>

              <div className="form-group">
                <label className="form-label">Country of residence</label>
                <select
                  className="form-input form-select"
                  value={formData.ownerCountry || ''}
                  onChange={handle('ownerCountry')}
                  disabled={isProcessing}
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Street address</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Unter den Linden 12, Apt. 3"
                  value={formData.ownerAddress || ''}
                  onChange={handle('ownerAddress')}
                  disabled={isProcessing}
                />
              </div>

              {/* City + State + Postal code — all three needed for the form */}
              <div className="form-row-3-col">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Berlin"
                    value={formData.city || ''}
                    onChange={handle('city')}
                    disabled={isProcessing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">State / Province</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Bavaria"
                    value={formData.stateCode || ''}
                    onChange={handle('stateCode')}
                    disabled={isProcessing}
                    maxLength={50}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Postal code</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="10115"
                    value={formData.postalCode || ''}
                    onChange={handle('postalCode')}
                    disabled={isProcessing}
                    maxLength={12}
                  />
                </div>
              </div>


            </div>
          </div>
        </div>

        {/* ── Bank statement panel — full width below the grid ──── */}
        <div className="form-panel">
          <div className="form-panel-header">
            <span className="form-panel-dot" />
            <span className="form-panel-title">Bank statement</span>
          </div>
          <div className="form-panel-body">

            {formData.statementText ? (
              <div className="file-loaded-row">
                <div className="file-loaded-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14 2 14 8 20 8"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="file-loaded-info">
                  <span className="file-loaded-name">{formData.statementName}</span>
                  <span className="file-loaded-size">{formatBytes(formData.statementSize)}</span>
                </div>
                <div className="file-loaded-badge">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                  </svg>
                  Ready
                </div>
                {!isProcessing && (
                  <button className="file-remove-btn" onClick={removeFile} type="button">Remove</button>
                )}
              </div>
            ) : (
              <div
                className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => !isParsing && fileInputRef.current?.click()}
                style={{ cursor: isParsing ? 'default' : 'pointer' }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  onChange={onFileInput}
                  disabled={isParsing}
                />
                {isParsing ? (
                  <>
                    <div className="spinner-small" style={{ margin: '0 auto 10px' }} />
                    <p className="file-drop-label">Reading PDF…</p>
                    <span className="file-drop-hint">Extracting text from your statement</span>
                  </>
                ) : (
                  <>
                    <div className="file-drop-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
                          stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p className="file-drop-label">Drop your bank statement PDF here</p>
                    <span className="file-drop-hint">or click to browse · PDF only · max 10MB</span>
                  </>
                )}
              </div>
            )}

            {fileError && (
              <div className="file-error-msg">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {fileError}
              </div>
            )}

            <p className="file-privacy-note">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Your statement is processed securely and never stored long-term.
            </p>

          </div>
        </div>

        {/* ── Footer bar + submit ───────────────────────────────── */}
        <div className="form-footer-bar">
          <div className="form-footer-left">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            IRS Form 5472 · Faxed directly to the IRS with delivery confirmation
          </div>
          <button
            className="btn-generate"
            onClick={onSubmit}
            disabled={isProcessing || isParsing || !isFormValid || submitBlocked || usedAllFiling}
          >
            {isProcessing ? (
              <><div className="spinner-small" /> Analyzing statement…</>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 2 14 8 20 8"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Generate
              </>
            )}
          </button>
        </div>

        {!planLoading && user && submitBlocked && (
          <div className="limit-upgrade-card">
            <div className="limit-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="limit-content">
              <h4>Purchase to file</h4>
              <p>$50 per filing · $399 for lifetime unlimited access</p>
            </div>
            <button className="upgrade-btn-card" onClick={() => navigate('/pricing-plans')}>
              Get started — $50
            </button>
          </div>
        )}

        {!planLoading && user && usedAllFiling && (
          <div className="limit-upgrade-card">
            <div className="limit-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="limit-content">
              <h4>Filing used</h4>
              <p>Purchase another single filing or upgrade to Lifetime for unlimited.</p>
            </div>
            <button className="upgrade-btn-card" onClick={() => navigate('/pricing-plans')}>
              Upgrade to Lifetime — $399
            </button>
          </div>
        )}

        {processingError && !submitBlocked && !usedAllFiling && (
          <div className="error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {processingError}
          </div>
        )}

      </div>
    </div>
  );
}